import { useSearchParams } from "react-router";

interface Props {
  top: number;
}

export function useWorkflowSearchParams({ top }: Readonly<Props>) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract sort values
  const sortField =
    searchParams.get("sortField") === "createdDate"
      ? "createdDate"
      : "updatedDate"; // default updatedDate
  const sortDir = searchParams.get("sortDir") === "asc" ? "asc" : "desc"; // default desc
  const orderBy = `${
    sortField === "createdDate" ? "createdOn" : "modifiedOn"
  } ${sortDir}`;

  // Extract pagination values
  const pageParam = searchParams.get("page");
  const pageNumber = pageParam
    ? Math.max(Number.parseInt(pageParam, 10), 1)
    : 1;
  const pagination = { top, skip: (pageNumber - 1) * top };

  // Extract status values
  const statuses = searchParams.getAll("status");
  const statusFilters = statuses.length
    ? statuses.map((s) => `status eq '${s}'`)
    : [];

  // Extract workflow definition values
  const definitionNames = searchParams.getAll("workflowDefinitionName");
  const definitionVersions = searchParams.getAll("workflowDefinitionVersion");
  const workflowDefinitionFilterCount = Math.min(
    definitionNames.length,
    definitionVersions.length,
  );
  const workflowDefinitionFilters = workflowDefinitionFilterCount
    ? Array.from({ length: workflowDefinitionFilterCount }, (_, index) => {
        return `workflowDefinition/workflowDefinitionName eq '${definitionNames[index]}' and workflowDefinition/version eq '${definitionVersions[index]}'`;
      })
    : [];

  function handlePrevious() {
    const currentPage = pageNumber;
    if (currentPage <= 2) {
      setSearchParams((prev) => {
        prev.delete("page");
        return prev;
      });
    } else {
      setSearchParams((prev) => {
        prev.set("page", String(currentPage - 1));
        return prev;
      });
    }
  }

  function handleNext() {
    const nextPage = pageNumber + 1;
    setSearchParams((prev) => {
      prev.set("page", String(nextPage));
      return prev;
    });
  }

  return {
    orderBy,
    pagination,
    statusFilters,
    workflowDefinitionFilters,
    handlePrevious,
    handleNext,
  };
}
